import { useState } from 'react';
import type { ReactNode } from 'react';

interface ConfirmModalProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export default function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // Guards against double-submission: a rapid second click (or a slow await
  // in onConfirm) must not re-fire the action, and the modal must not look
  // frozen while it's in flight.
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } catch (err) {
      // Swallow here — the modal's open/closed state is entirely driven by
      // the caller's own confirmAction wiring (it only clears on success),
      // so re-throwing would just surface as an unhandled rejection with no
      // additional effect. Reset busy regardless so the modal never sticks.
      console.error('ConfirmModal: onConfirm failed', err);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    onCancel();
  };

  return (
    <div
      data-testid="confirm-modal"
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={handleCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-warm-900">{title}</h3>
        <div className="text-sm text-warm-600 mt-2">{message}</div>
        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy}
            className="px-4 py-2 min-h-[44px] text-sm font-medium border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`px-4 py-2 min-h-[44px] text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
              confirmVariant === 'danger'
                ? 'bg-danger-600 hover:bg-danger-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {busy ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
